import { useState } from "react";
import { App, Empty, Input } from "antd5";
import { Text } from "styles/utility-components";

import { TextLink } from "components/actions/Links";
import { RouterTextLink } from "components/actions/RouterLink";
import { DetailsHeader, DetailsPage, DetailsSection } from "components/app_layout/DetailsLayout";
import { PaywallBanner } from "components/paywall/PaywallBanner";
import { Markdown } from "components/reports/builder_components/Markdown";
import Button from "components/ui/button/Button";
import { appRoutes } from "lib/appRoutes";
import { copyToClipboard } from "lib/clipboard";
import { stringSort } from "lib/columnSort";
import { Table } from "lib/core_components/Table";
import { useFileExport } from "lib/hooks/api/exports/useFileExport";
import { type CompanyReport, useCompanyReports } from "lib/hooks/api/reports/useCompanyReports";
import { useExportReports } from "lib/hooks/api/reports/useExportReport";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { EventNames, useTracking } from "lib/tracking";
import { downloadFileViaFetch, formatDate } from "lib/utils";

import css from "./CustomBriefingsPage.module.scss";

import SPARKLES from "../../../assets/images/ai_stars.png";
import AccountBriefingImage from "../../../assets/images/paywall/Account_Briefing_Paywall.svg";

export function formatReportType(reportType: string) {
  // reportTypes are snake_case coming in from the db
  return (
    reportType
      // Replace underscores with spaces
      .replace(/_/g, " ")
      // If the word is "ai" replace it with "AI"
      .replace(/\bai\b/g, "AI")
      // Capitalize the first letter of each word
      .replace(/\b\w/g, (l) => l.toUpperCase())
  );
}

export function CustomBriefingsPage() {
  const user = useAssertCurrentUser();

  const { data, isLoading, isError } = useCompanyReports(user.company.guid);
  const [searchText, setSearhText] = useState<string>();

  let reports = data || [];

  if (!user.hasAccountBriefingPermission && reports.length === 0) {
    return <BriefingsPaywall />;
  }

  if (searchText) {
    reports = reports.filter((r) => r.title.toLowerCase().includes(searchText.toLowerCase()));
  }

  return (
    <DetailsPage>
      <DetailsHeader className={css.header} borderBottom={false}>
        <Text h1>Custom Briefings {data && `(${data.length})`}</Text>
        <Input.Search
          placeholder="Search"
          className={css.search}
          onChange={(e) => setSearhText(e.target.value)}
          value={searchText}
        />
      </DetailsHeader>
      {data && data.length === 0 ? (
        <div>
          <Empty
            description="Custom briefings will appear here"
            image={<img src={AccountBriefingImage} />}
          >
            {/* TODO: enable request reports requires new api */}
            {/* {user.has_account_briefing_permission && (
              <Button >Request briefing</Button>
            )} */}
          </Empty>
        </div>
      ) : (
        <DetailsSection noBorder>
          <Table
            columns={[
              {
                key: "title",
                title: "Title",
                dataIndex: "title",
                sorter: stringSort((r) => r.title),
                render: (_: unknown, r: CompanyReport) => (
                  <>
                    <RouterTextLink {...appRoutes.reportDetail(r.id)}>
                      {r.title}
                      {r.reportType === "ai_report" && (
                        <img src={SPARKLES} alt="AI marker" height="20" className={css.aiMarker} />
                      )}
                    </RouterTextLink>
                    {r.description && (
                      <div className={css.descriptionContainer}>
                        <Markdown text={r.description} />
                      </div>
                    )}
                  </>
                ),
              },
              {
                key: "publishedAt",
                title: "Published date",
                dataIndex: "publishedAt",
                sorter: stringSort((r) => r.publishedAt),
                render: (_, record) =>
                  record.publishedAt
                    ? formatDate(new Date(record.publishedAt))
                    : record.publishedAt,
                width: "20%",
              },
              {
                key: "reportType",
                title: "Report type",
                dataIndex: "reportType",
                sorter: stringSort((r) => r.reportType),
                width: "20%",
                render(_, record) {
                  return formatReportType(record.reportType);
                },
              },
              {
                key: "actions",
                title: "Actions",
                render: (_, record) => <BriefingActions record={record} />,
                width: "20%",
              },
            ]}
            dataSource={reports}
            loading={isLoading}
            isError={isError}
            rowKey={"id"}
          />
        </DetailsSection>
      )}
    </DetailsPage>
  );
}

function BriefingActions({ record }: { record: CompanyReport }) {
  const { message } = App.useApp();
  const { logEvent } = useTracking();

  const exportMutation = useExportReports({
    onError: () => {
      message.error(`Failed to download report "${record.title}"`);
    },
  });
  const { mutate: mutateDownload } = exportMutation;

  const { isExporting: isDownloading } = useFileExport({
    mutation: exportMutation,
    getFileName: () => `${record.title}.pdf`,
    onError: () => {
      message.error(`Failed to download report "${record.title}"`);
    },
  });

  const copyReportLink = async (reportId: string, title: string) => {
    const clipboardContent = {
      plain: `https://app.stotles.com/reports/${reportId}?utm_source=share`,
    };
    const copiedToClipboard = await copyToClipboard(clipboardContent);
    if (copiedToClipboard) {
      void message.success("Copied!", 2);
    } else {
      void message.error(`Could not copy link to clipboard`);
    }
    logEvent(EventNames.customBriefingLinkCopied, {
      "Report name": title,
      "Report URL": `/reports/${reportId}`,
    });
  };

  return (
    <div className={css.actionsContainer}>
      <Button
        as="a"
        icon="share03"
        {...appRoutes.reportDetail(record.id)}
        onClick={(e) => {
          e.stopPropagation();
          logEvent(EventNames.customBriefingViewed, {
            "Report name": record.title,
            "Report URL": `/reports/${record.id}`,
          });
        }}
      >
        View
      </Button>{" "}
      <Button
        buttonType="secondary"
        title="Download"
        icon="download01"
        loading={isDownloading}
        onClick={(e) => {
          e.stopPropagation();
          logEvent(EventNames.customBriefingDownloaded, {
            "Report name": record.title,
            "Report URL": `/reports/${record.id}`,
          });
          // Reports that are just an existing PDF serve that file rather than a print of the page
          if (record.pdfUrl) {
            void downloadFileViaFetch(record.pdfUrl, `${record.title}.pdf`);
          } else {
            mutateDownload({ id: record.id });
          }
        }}
      />{" "}
      <Button
        buttonType="secondary"
        title="Copy link"
        icon="link01"
        onClick={async () => copyReportLink(record.id, record.title)}
      />
    </div>
  );
}

function BriefingsPaywall() {
  return (
    <DetailsPage>
      <DetailsHeader className={css.header} borderBottom={false}>
        <Text h1>Custom Briefings</Text>
      </DetailsHeader>
      <PaywallBanner featureType="ACCOUNT_BRIEFINGS" />
      <DetailsSection>
        <Table
          dataSource={EXAMPLE_REPORTS_DATA}
          isError={false}
          columns={[
            {
              key: "title",
              title: "Title",
              render: (_, r) => (
                <>
                  <TextLink to={r.link} targetType="new-tab">
                    {r.title}
                  </TextLink>
                  {r.description && (
                    <div className={css.descriptionContainer}>
                      <Markdown text={r.description} />
                    </div>
                  )}
                </>
              ),
            },
            {
              key: "publishedAt",
              title: "Published date",
              dataIndex: "publishedAt",
              sorter: stringSort((r) => r.publishedAt),
              render: (_, record) =>
                record.publishedAt ? formatDate(new Date(record.publishedAt)) : record.publishedAt,
              width: "20%",
            },
            {
              key: "actions",
              title: "Actions",
              render: (_, r) => (
                <Button
                  as="a"
                  className={css.primaryBtn}
                  icon="share03"
                  href={r.link}
                  target="new-tab"
                >
                  View example
                </Button>
              ),
              width: "20%",
            },
          ]}
          rowKey={"link"}
        />
      </DetailsSection>
    </DetailsPage>
  );
}

export const EXAMPLE_REPORTS_DATA = [
  {
    title: "Account briefing",
    description:
      "A deep-dive on buyer organisations highlighting upcoming opportunities and key supplier relationships.",
    publishedAt: "2022-05-17",
    link: "https://app.stotles.com/reports/87a930f6-5414-481e-91be-8efe7db5c72a",
  },
  {
    title: "Competitor Briefing",
    description:
      "Gain insights into competitor movement, key buyer relationships and upcoming contract renewals.",
    publishedAt: "2022-08-02",
    link: "https://app.stotles.com/reports/4901638e-d3a6-4957-a59d-268d4b41a804",
  },
  {
    title: "Opportunity Briefing",
    description:
      "Access key datapoints on opportunities you’re interested in and qualify their importance.",
    publishedAt: "2022-02-15",
    link: "https://app.stotles.com/reports/e12deab6-3988-4679-be96-b2248169bbe1",
  },
  {
    title: "Weekly Prospecting Briefing",
    description:
      "Receive weekly summaries of relevant events like new contracts or competitor and partner activity.",
    publishedAt: "2020-04-23",
    link: "https://app.stotles.com/reports/6e60664f-73cc-4d44-a339-033ad7e4dec2",
  },
  {
    title: "Persona Briefing",
    description:
      "Learn everything you need to know about a particular decision maker within your key account(s).",
    publishedAt: "2022-08-29",
    link: "https://app.stotles.com/reports/195b1bdb-c446-434b-b3e5-5001435f96f5",
  },
  {
    title: "Partner Briefing",
    description:
      "Analyse a partner organisation/s to identify potential opportunities they’re working on.",
    publishedAt: "2021-12-15",
    link: "https://app.stotles.com/reports/53595160-a3c1-4bd8-8f1f-ddb28c782386",
  },
  {
    title: "Management Briefing",
    description:
      "Get an overview of relevant market opportunities and understand the best path to involvement.",
    publishedAt: "2022-01-23",
    link: "https://app.stotles.com/reports/72fbfa7a-d373-4ae8-b4bf-427628945606",
  },
  {
    title: "Competitor Landscape Analysis",
    description:
      "Identify competitor impact across buyers to qualify organisations worth reaching out to.",
    publishedAt: "2022-08-31",
    link: "https://app.stotles.com/reports/9f78af73-062f-413c-b3d6-424f03e31364",
  },
];
